import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Link } from 'react-router-dom';
import { MessageCircle, Download, Tv, Info, Settings, ChevronRight, Film, Megaphone } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useWS } from '../context/WSContext';
import api from '../lib/api';
import PushEnableBanner from '../components/PushEnableBanner';
import TrendingSlider from '../components/TrendingSlider';
import SurpriseMeCard from '../components/SurpriseMeCard';

// Metadata for each Home slider section — keyed by the id stored in home_layout settings.
const SECTION_META = {
  latest:         { title: 'Ultime Uscite',        emoji: '🆕', endpoint: '/tmdb/latest-releases', testKey: 'latest' },
  trending_movie: { title: 'Film di tendenza',     emoji: '🎬', endpoint: '/tmdb/trending/movie',  testKey: 'movie', mediaTypeFallback: 'movie' },
  trending_tv:    { title: 'Serie TV di tendenza', emoji: '📺', endpoint: '/tmdb/trending/tv',     testKey: 'tv',    mediaTypeFallback: 'tv' },
  top_rated:      { title: 'Più Votati',           emoji: '🏆', endpoint: '/tmdb/top-rated',       testKey: 'top' },
};

const sections = [
  { to: '/chat', title: 'Assistenza', desc: 'Chat privata con lo staff', icon: MessageCircle, tint: 'from-[var(--gold)]/25 to-transparent', badgeKey: 'chat' },
  { to: '/comunicazioni', title: 'Comunicazioni Importanti', desc: 'Aggiornamenti ufficiali e novità', icon: Megaphone, tint: 'from-red-500/20 to-transparent', badgeKey: 'broadcast' },
  { to: '/novita', title: 'Novità Contenuti', desc: 'Nuovi film e serie sui nostri server', icon: Film, tint: 'from-[var(--gold)]/20 to-transparent', badgeKey: 'novita' },
  { to: '/guida', title: 'Scarica le App', desc: 'Codice Downloader e installazione', icon: Download, tint: 'from-white/10 to-transparent' },
  { to: '/apps', title: 'Le mie App', desc: 'Accedi alle tue applicazioni', icon: Tv, tint: 'from-[var(--gold)]/15 to-transparent' },
  { to: '/info', title: 'Informazioni', desc: 'Su Golden TV Connect', icon: Info, tint: 'from-white/5 to-transparent' },
  { to: '/impostazioni', title: 'Impostazioni', desc: 'Preferenze account', icon: Settings, tint: 'from-white/5 to-transparent' },
];

export default function Home() {
  const { user } = useAuth();
  const { subscribe } = useWS();
  const [unseen, setUnseen] = useState(0);
  const [unread, setUnread] = useState(0);
  const [unseenBc, setUnseenBc] = useState(0);
  const [homeLayout, setHomeLayout] = useState(
    // Default fallback so the sliders render even if the settings fetch fails
    Object.keys(SECTION_META).map((k) => ({ key: k, enabled: true, limit: 20 }))
  );

  const refresh = async () => {
    const sinceNovita = localStorage.getItem('gtc_last_novita_seen') || '1970-01-01T00:00:00Z';
    const sinceBc = localStorage.getItem('gtc_last_broadcast_seen') || '1970-01-01T00:00:00Z';
    try {
      const [p, e, u, b] = await Promise.all([
        api.get('/contents', { params: { server: 'prime' } }),
        api.get('/contents', { params: { server: 'elite' } }),
        api.get('/messages/unread-count'),
        api.get('/broadcasts'),
      ]);
      const all = [...p.data, ...e.data];
      const n = all.filter((c) => !c.is_hidden && c.created_at > sinceNovita).length;
      setUnseen(n);
      setUnread(u.data.count || 0);
      setUnseenBc(b.data.filter((x) => x.created_at > sinceBc).length);
    } catch { /* ignore */ }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    let cancel = false;
    api.get('/home-layout').then(({ data }) => {
      if (!cancel && Array.isArray(data.sections)) setHomeLayout(data.sections);
    }).catch(() => { /* keep default */ });
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    const unsub = subscribe((data) => {
      if (data.type === 'new_content') refresh();
      if (data.type === 'message') refresh();
      if (data.type === 'read_receipt') refresh();
      if (data.type === 'broadcast') refresh();
    });
    return unsub;
    // eslint-disable-next-line
  }, []);

  return (
    <Layout title="Home">
      <div className="px-5 pt-6 max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-white/40">Benvenuto</p>
          <h2 className="font-heading text-3xl font-light mt-1">
            Ciao, <span className="text-[var(--gold)]">{user?.username}</span>
          </h2>
          <p className="text-sm text-white/50 mt-2">Cosa vuoi fare oggi?</p>
        </motion.div>

        <div className="mb-4">
          <PushEnableBanner />
        </div>

        <SurpriseMeCard />

        <div className="space-y-3">
          {sections.map((s, i) => (
            <motion.div
              key={s.to}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i, duration: 0.35 }}
            >
              <Link
                to={s.to}
                data-testid={`home-card-${s.to.slice(1)}`}
                className="card-surface p-4 flex items-center gap-4 group block relative overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${s.tint} opacity-60 pointer-events-none`} />
                <div className="relative w-12 h-12 rounded-2xl bg-[var(--bg-elevated)] border border-[var(--border-subtle)] flex items-center justify-center group-hover:border-[var(--gold)]/50 transition-colors">
                  <s.icon className="w-5 h-5 text-[var(--gold)]" strokeWidth={1.7} />
                </div>
                <div className="relative flex-1 min-w-0">
                  <div className="font-heading text-base font-medium text-white flex items-center gap-2">
                    {s.title}
                    {s.badgeKey === 'novita' && unseen > 0 && (
                      <span
                        data-testid="novita-unseen-badge"
                        className="text-[10px] bg-[var(--gold)] text-black rounded-full px-2 py-0.5 font-bold min-w-[20px] text-center pulse-gold"
                      >
                        {unseen > 99 ? '99+' : unseen}
                      </span>
                    )}
                    {s.badgeKey === 'chat' && unread > 0 && (
                      <span
                        data-testid="chat-unread-badge"
                        className="text-[10px] bg-[var(--gold)] text-black rounded-full px-2 py-0.5 font-bold min-w-[20px] text-center pulse-gold"
                      >
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                    {s.badgeKey === 'broadcast' && unseenBc > 0 && (
                      <>
                        <span
                          data-testid="broadcast-new-badge"
                          className="text-[9px] bg-red-500 text-white rounded-full px-2 py-0.5 font-bold tracking-wide"
                        >
                          NUOVO
                        </span>
                        <span
                          data-testid="broadcast-unseen-badge"
                          className="text-[10px] bg-[var(--gold)] text-black rounded-full px-2 py-0.5 font-bold min-w-[20px] text-center pulse-gold"
                        >
                          {unseenBc > 99 ? '99+' : unseenBc}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">{s.desc}</div>
                </div>
                <ChevronRight className="relative w-5 h-5 text-white/30 group-hover:text-[var(--gold)] transition-colors" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* TMDB trending sections — full bleed (no max-w constraint) */}
      <div className="mt-2 -mx-5 sm:mx-0">
        {homeLayout
          .filter((s) => s.enabled && SECTION_META[s.key])
          .map((s) => {
            const meta = SECTION_META[s.key];
            const url = `${meta.endpoint}?limit=${s.limit || 20}`;
            return (
              <TrendingSlider
                key={s.key}
                endpoint={url}
                testKey={meta.testKey}
                title={meta.title}
                emoji={meta.emoji}
                mediaTypeFallback={meta.mediaTypeFallback}
              />
            );
          })}
      </div>
    </Layout>
  );
}
